import { useMutation, useQueryClient } from '@tanstack/react-query'
import { saveGoalField } from '../data/goalsApi.js'
import { useHistoryStore } from '../store/historyStore.js'
import { withArchiveGuard } from './_archiveGuard.js'

/**
 * useUpdateGoal — mutation hook for changing a single field on a goal
 * or sub-goal.
 *
 * Input shape:
 *   { pillarName, goalIndex, subgoalIndex?, field, value }
 *
 * Concurrency model:
 *   The server's /goals/field endpoint does a read-modify-write for
 *   that one field on the live KV state. So if two teammates edit
 *   different fields (or different sub-goals / different goals) at the
 *   same time, neither clobbers the other — each change is applied in
 *   order to the most recent snapshot of goals.
 *
 *   Previously this hook POSTed the ENTIRE goals array via
 *   saveGoalsData, which meant the last writer within a few-second
 *   window would blow away any concurrent edits. Fixed 2026-04-24.
 *
 * Flow:
 *   1. onMutate — write the field change optimistically to the cache
 *      so the UI updates instantly. Push an undo snapshot.
 *   2. mutationFn — POST just the one field to /goals/field.
 *   3. onSuccess — take the authoritative goals array returned by the
 *      server and overwrite the cache with it. This pulls in any
 *      concurrent edits made by teammates in the same window.
 *   4. onError — roll back the optimistic cache entry.
 */
export function useUpdateGoal() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (args) => saveGoalField(args),

    onMutate: async ({ pillarName, goalIndex, subgoalIndex, field, value }) => {
      await queryClient.cancelQueries({ queryKey: ['goals'] })
      const previous = queryClient.getQueryData(['goals'])

      // Capture the specific prior value so undo can be a TARGETED
      // field revert via /goals/field rather than a full-state POST.
      // Full-state undo would risk clobbering teammates' concurrent
      // edits on unrelated fields — the targeted form is clobber-safe.
      let prevValue
      try {
        const pi = (previous || []).findIndex((p) => p.pillar === pillarName)
        const goal = pi >= 0 ? previous[pi].goals?.[goalIndex] : null
        if (goal) {
          if (typeof subgoalIndex === 'number') {
            const sub = (goal.subgoals || goal.subGoals)?.[subgoalIndex]
            if (sub) prevValue = sub[field]
          } else {
            prevValue = goal[field]
          }
        }
      } catch {
        prevValue = undefined
      }

      useHistoryStore
        .getState()
        .pushSnapshot(
          `Edit ${field} on ${pillarName}`,
          undefined, // no full-state goals snapshot — revert is targeted
          queryClient.getQueryData(['meta']),
          {
            pillarName,
            goalIndex,
            subgoalIndex,
            field,
            value: prevValue,
          }
        )

      queryClient.setQueryData(['goals'], (old) => {
        if (!old) return old
        const next = structuredClone(old)
        const pi = next.findIndex((p) => p.pillar === pillarName)
        if (pi < 0) return old
        const goal = next[pi].goals?.[goalIndex]
        if (!goal) return old
        if (typeof subgoalIndex === 'number') {
          const sub = (goal.subgoals || goal.subGoals)?.[subgoalIndex]
          if (sub) sub[field] = value
        } else {
          goal[field] = value
        }
        return next
      })

      return { previous }
    },

    onSuccess: (serverGoals) => {
      // Server returns the post-mutation array — includes any
      // concurrent edits from teammates. Replacing the cache with it
      // is how we stay consistent without needing a full refetch.
      if (Array.isArray(serverGoals)) {
        queryClient.setQueryData(['goals'], serverGoals)
      }
    },

    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['goals'], ctx.previous)
    },
  })
  return withArchiveGuard(mutation)
}
